/* @jsxImportSource @emotion/react */
import React, { type ReactNode, useEffect, useState } from 'react'
import { keyframes } from '@emotion/react'
import classNames from 'classnames'

import { CheckmarkIcon, SliteAgentIcon } from '@slite/icons'

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`

// Default duration a tool step's running-pulse takes before settling.
// Shared with consumers that need to wait the same amount (DemoAgent's
// blockDurationMs, storyController's signal/scan beat).
export const TOOL_DURATION_MS = 2000

// Char-by-char reveal cadence for the AI-streaming effect.
const STREAM_CHAR_MS = 22

export const AgentRow = ({
  icon,
  title,
  pulse = true,
  stream = false,
  durationMs,
  children,
}: {
  icon?: ReactNode
  title: string
  pulse?: boolean
  stream?: boolean
  durationMs?: number
  children?: ReactNode
}) => {
  const [done, setDone] = useState(!pulse)
  const [visibleChars, setVisibleChars] = useState(stream ? 0 : title.length)

  useEffect(() => {
    if (!pulse) {
      setDone(true)
      return
    }
    setDone(false)
    const t = setTimeout(() => setDone(true), durationMs ?? TOOL_DURATION_MS)
    return () => clearTimeout(t)
  }, [pulse, durationMs])

  useEffect(() => {
    if (!stream) {
      setVisibleChars(title.length)
      return
    }
    setVisibleChars(0)
    const id = setInterval(() => {
      setVisibleChars((n) => {
        if (n >= title.length) {
          clearInterval(id)
          return n
        }
        return n + 1
      })
    }, STREAM_CHAR_MS)
    return () => clearInterval(id)
  }, [stream, title])

  const isRunning = pulse && !done
  const showCheck = pulse && done
  const isStreaming = stream && visibleChars < title.length
  const displayedTitle = stream ? title.slice(0, visibleChars) : title

  return (
    <div className="flexbox flexbox-column" style={{ minWidth: 0 }}>
      <div
        className={classNames(
          'flexbox flexbox-align-center shade-10',
          isRunning && 'font-weight-medium super-gradient animated'
        )}
        style={{
          fontSize: 15,
          lineHeight: '1.5em',
          minWidth: 0,
          fontWeight: 500,
          color: isRunning ? undefined : 'var(--shade-00)',
        }}
      >
        <span
          className={classNames(
            'inline-block margin-right-8 flexbox-no-shrink',
            isRunning ? 'super-gradient-left-animated' : 'shade-20'
          )}
          style={{ display: 'inline-flex' }}
        >
          {icon ?? (
            <SliteAgentIcon
              size={16}
              style={{ color: 'var(--saturn-orange)' }}
            />
          )}
        </span>
        <span style={{ minWidth: 0 }}>
          {displayedTitle}
          {isStreaming && (
            <span
              aria-hidden
              className="margin-left-2"
              css={{
                display: 'inline-block',
                width: 2,
                height: '0.95em',
                verticalAlign: '-0.1em',
                backgroundColor: 'currentColor',
                animation: `${caretBlink} 0.9s infinite`,
              }}
            />
          )}
        </span>
        {showCheck && (
          <CheckmarkIcon size={10} className="primary-light margin-left-4" />
        )}
      </div>
      {children && (
        <div className="flexbox flexbox-gap-8 padding-top-8">
          <div
            className="flexbox flexbox-centered flexbox-no-shrink"
            style={{ width: 14 }}
          >
            <div
              style={{
                width: 1,
                height: '100%',
                background:
                  'linear-gradient(180deg, var(--shade-45) 0%, var(--shade-45) 50%, #ECEDEF00 100%)',
              }}
            />
          </div>
          <div
            className="flexbox flexbox-column flexbox-gap-8 width-full"
            style={{ overflow: 'hidden', minWidth: 0 }}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
